import { FormEvent, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  ENROLL_POLICY_TRIGGER_KINDS,
  type EnrollPolicyConditions,
  type EnrollPolicyTriggerKind,
} from '@marketing-hub/shared';
import { apiFetch } from '@/api/client';
import { Button } from '@/components/ui/Button';
import { Field, TextInput } from '@/components/ui/Field';
import { LoadingState } from '@/components/StateMessage/StateMessage';
import styles from '@/features/automations/automations.module.css';
import pageStyles from '@/pages/ProjectsPage.module.css';

type PolicyRow = {
  id: string;
  name: string;
  enabled: boolean;
  priority: number;
  trigger_kind: EnrollPolicyTriggerKind;
  conditions: EnrollPolicyConditions;
  campaign_id: string | null;
  campaign_name: string | null;
  campaign_resolved_name: string | null;
};

type Props = {
  projectId: string;
};

function tagsToString(conditions: EnrollPolicyConditions): string {
  return (conditions.skip_if_tags ?? []).join(', ');
}

function parseTags(raw: string): string[] {
  return raw
    .split(',')
    .map((t) => t.trim())
    .filter(Boolean);
}

export function EnrollPoliciesPanel({ projectId }: Props) {
  const qc = useQueryClient();
  const [newName, setNewName] = useState('Inbound nurture');
  const [newPriority, setNewPriority] = useState('100');
  const [newCampaignId, setNewCampaignId] = useState('');
  const [newSkipTags, setNewSkipTags] = useState('enroll-block');
  const [newTrigger, setNewTrigger] = useState<EnrollPolicyTriggerKind>('inbound_request');

  const policiesQ = useQuery({
    queryKey: ['enroll-policies', projectId],
    queryFn: () =>
      apiFetch<{ policies: PolicyRow[] }>(`/api/enroll-policies?project_id=${projectId}`),
  });

  const campaignsQ = useQuery({
    queryKey: ['campaigns-list', projectId],
    queryFn: () =>
      apiFetch<{ campaigns: { id: string; name: string; status: string }[] }>(
        `/api/campaigns?project_id=${projectId}`,
      ),
  });

  const invalidate = () => void qc.invalidateQueries({ queryKey: ['enroll-policies', projectId] });

  const createM = useMutation({
    mutationFn: () =>
      apiFetch('/api/enroll-policies', {
        method: 'POST',
        body: JSON.stringify({
          project_id: projectId,
          name: newName,
          priority: Number(newPriority) || 0,
          trigger_kind: newTrigger,
          campaign_id: newCampaignId.trim() || undefined,
          conditions: { skip_if_tags: parseTags(newSkipTags) },
        }),
      }),
    onSuccess: invalidate,
  });

  const patchM = useMutation({
    mutationFn: (payload: { id: string; body: Record<string, unknown> }) =>
      apiFetch(`/api/enroll-policies/${payload.id}`, {
        method: 'PATCH',
        body: JSON.stringify(payload.body),
      }),
    onSuccess: invalidate,
  });

  const deleteM = useMutation({
    mutationFn: (id: string) => apiFetch(`/api/enroll-policies/${id}`, { method: 'DELETE' }),
    onSuccess: invalidate,
  });

  if (policiesQ.isLoading) return <LoadingState label="Policies laden…" />;

  const policies = policiesQ.data?.policies ?? [];
  const campaigns = campaignsQ.data?.campaigns ?? [];

  return (
    <section className={pageStyles.card}>
      <h2>Auto-enroll policies</h2>
      <p className={pageStyles.mutedSmall}>
        Hogere priority wint. Tag <code>enroll-block</code> skipt enroll (guard template). Zie{' '}
        <code>reply_skip_enroll_guard</code> in automation templates.
      </p>

      <div className={styles.policyList}>
        {policies.map((p) => (
          <PolicyCard
            key={p.id}
            policy={p}
            campaigns={campaigns}
            onPatch={(body) => patchM.mutate({ id: p.id, body })}
            onDelete={() => {
              if (window.confirm(`Policy "${p.name}" verwijderen?`)) deleteM.mutate(p.id);
            }}
          />
        ))}
        {!policies.length ? <p className={pageStyles.mutedSmall}>Nog geen policies.</p> : null}
      </div>

      <h3>Nieuwe policy</h3>
      <form
        className={pageStyles.formGrid}
        onSubmit={(e: FormEvent) => {
          e.preventDefault();
          createM.mutate();
        }}
      >
        <Field label="Naam">
          <TextInput value={newName} onChange={(e) => setNewName(e.target.value)} required />
        </Field>
        <Field label="Priority">
          <TextInput value={newPriority} onChange={(e) => setNewPriority(e.target.value)} />
        </Field>
        <Field label="Trigger">
          <select
            className={pageStyles.filterInput}
            value={newTrigger}
            onChange={(e) => setNewTrigger(e.target.value as EnrollPolicyTriggerKind)}
          >
            {ENROLL_POLICY_TRIGGER_KINDS.map((k) => (
              <option key={k} value={k}>
                {k}
              </option>
            ))}
          </select>
        </Field>
        <Field label="Campagne">
          <select
            className={pageStyles.filterInput}
            value={newCampaignId}
            onChange={(e) => setNewCampaignId(e.target.value)}
            required
          >
            <option value="">— kies —</option>
            {campaigns.map((c) => (
              <option key={c.id} value={c.id}>
                {c.name} ({c.status})
              </option>
            ))}
          </select>
        </Field>
        <Field label="skip_if_tags (komma)">
          <TextInput value={newSkipTags} onChange={(e) => setNewSkipTags(e.target.value)} />
        </Field>
        <Button type="submit" disabled={createM.isPending}>
          Policy toevoegen
        </Button>
      </form>
    </section>
  );
}

function PolicyCard({
  policy,
  campaigns,
  onPatch,
  onDelete,
}: {
  policy: PolicyRow;
  campaigns: { id: string; name: string; status: string }[];
  onPatch: (body: Record<string, unknown>) => void;
  onDelete: () => void;
}) {
  const [name, setName] = useState(policy.name);
  const [priority, setPriority] = useState(String(policy.priority));
  const [campaignId, setCampaignId] = useState(policy.campaign_id ?? '');
  const [skipTags, setSkipTags] = useState(tagsToString(policy.conditions ?? {}));
  const [enabled, setEnabled] = useState(policy.enabled);

  const campaignLabel =
    policy.campaign_resolved_name ?? policy.campaign_name ?? policy.campaign_id ?? '—';

  return (
    <div className={styles.policyCard}>
      <div className={styles.policyCardHeader}>
        <strong>{policy.name}</strong>
        <span className={pageStyles.mutedSmall}>→ {campaignLabel}</span>
      </div>
      <div className={pageStyles.formGrid}>
        <Field label="Naam">
          <TextInput value={name} onChange={(e) => setName(e.target.value)} />
        </Field>
        <Field label="Priority">
          <TextInput value={priority} onChange={(e) => setPriority(e.target.value)} />
        </Field>
        <Field label="Campagne">
          <select
            className={pageStyles.filterInput}
            value={campaignId}
            onChange={(e) => setCampaignId(e.target.value)}
          >
            <option value="">— {policy.campaign_name ?? 'geen id'} —</option>
            {campaigns.map((c) => (
              <option key={c.id} value={c.id}>
                {c.name}
              </option>
            ))}
          </select>
        </Field>
        <Field label="skip_if_tags">
          <TextInput value={skipTags} onChange={(e) => setSkipTags(e.target.value)} />
        </Field>
        <label className={pageStyles.mutedSmall}>
          <input
            type="checkbox"
            checked={enabled}
            onChange={(e) => setEnabled(e.target.checked)}
          />{' '}
          Actief
        </label>
      </div>
      <div style={{ display: 'flex', gap: '0.5rem', flexWrap: 'wrap' }}>
        <Button
          type="button"
          variant="secondary"
          onClick={() =>
            onPatch({
              name: name.trim(),
              priority: Number(priority) || 0,
              enabled,
              campaign_id: campaignId.trim() || null,
              conditions: { ...policy.conditions, skip_if_tags: parseTags(skipTags) },
            })
          }
        >
          Opslaan
        </Button>
        <Button type="button" variant="ghost" onClick={onDelete}>
          Verwijderen
        </Button>
      </div>
    </div>
  );
}
